import {
  type Dispatch,
  type FunctionComponent,
  type SetStateAction,
  type SVGProps,
  useState,
} from "react";

import { MenuSearchInput } from "common/components/MenuSearchInput";
import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import FilterButton from "common/components/ui/FilterButton";
import FormField from "common/components/ui/FormField";
import { Input } from "common/components/ui/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectItemText,
  SelectTrigger,
  SelectValue,
} from "common/components/ui/Select";
import SelectButton from "common/components/ui/SelectButton";
import useCrmSources from "common/datahooks/useCrmSources";
import useIntegrationSettings from "common/datahooks/useIntegrationSettings";
import { HTTPError } from "common/helpers/HTTP";
import { typedObjectEntries } from "common/helpers/utils";
import type { OutreachSourceType } from "common/types";

import { useCampaignContext } from "../../../../../context/CampaignContext";
import useCampaignMutations from "../../../../../datahooks/useCampaignMutations";

import EmailBisonIcon from "assets/icons/emailBison.svg?react";
import InstantlyIcon from "assets/icons/instantly.svg?react";
import SmartleadIcon from "assets/icons/smartlead.svg?react";
import CrmBanner from "assets/images/crm-campaign.png";

const typeOptions: Record<
  OutreachSourceType,
  { label: string; icon: FunctionComponent<SVGProps<SVGSVGElement>> }
> = {
  instantly: { label: "Instantly", icon: InstantlyIcon },
  smartlead: { label: "Smartlead", icon: SmartleadIcon },
  emailbison: { label: "EmailBison", icon: EmailBisonIcon },
};

interface CrmSourceSelection {
  id: string;
  name: string;
  urlVariable?: string;
}

interface CrmListsProps {
  setSelectSource: Dispatch<SetStateAction<CrmSourceSelection | undefined>>;
}

interface SelectedSource {
  id: string;
  name: string;
}

export default function CrmSources({ setSelectSource }: CrmListsProps) {
  const [inputValue, setInputValue] = useState("");
  const { id: campaignId, crm_type: type } = useCampaignContext();
  const { updateCampaign } = useCampaignMutations();

  const [selectedSource, setSelectedSource] = useState<SelectedSource | null>(
    null,
  );
  const [linkedinUrlFieldName, setLinkedinUrlFieldName] = useState("");

  const { integrationSettings, isLoadingIntegrationSettings } =
    useIntegrationSettings();

  const sourceType = type === "instantly" ? "lists" : "campaigns";

  const { crmSources, iseLoadingCrmSources, crmSourcesError } = useCrmSources({
    type,
    sourceType,
  });

  function changeOutreachSourceType(newType: OutreachSourceType) {
    setSelectedSource(null);
    setLinkedinUrlFieldName("");
    setInputValue("");
    updateCampaign({ campaignId, updates: { crm_type: newType } });
  }

  function handleApply() {
    if (!selectedSource || !linkedinUrlFieldName.trim()) return;

    setSelectSource({
      ...selectedSource,
      urlVariable: linkedinUrlFieldName.trim(),
    });
  }

  const isEmailBison = type === "emailbison";
  const canApply = !!selectedSource && !!linkedinUrlFieldName.trim();

  const options = crmSources || [];
  const lowercasedInput = inputValue.toLowerCase();
  const filteredOptions = options.filter((option) =>
    option.name.toLowerCase().includes(lowercasedInput),
  );

  let dropdownContent = (
    <p className="p-4 text-center text-black-500">No {sourceType} found</p>
  );

  if (options.length) {
    dropdownContent =
      filteredOptions.length > 0 ? (
        <DropdownMenuList>
          {filteredOptions.map((option) => (
            <DropdownMenuItem
              key={option.id}
              onClick={() => {
                if (isEmailBison) {
                  setSelectedSource(option);
                } else {
                  setSelectSource(option);
                }
              }}
            >
              {option.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuList>
      ) : (
        <p className="break-words p-4 text-center text-black-500">
          No results found for&nbsp;
          <span className="text-black-950">{inputValue}</span>
        </p>
      );
  }

  return (
    <section className="max-w-4xl mx-auto flex w-full flex-col lg:rounded-5xl lg:border lg:border-black-200 lg:p-5 lg:shadow-md">
      <img
        src={CrmBanner}
        width={677}
        height={210}
        alt=""
        className="mb-5 rounded-2xl w-full"
      />

      <div className="flex flex-col gap-2.5 lg:flex-row">
        <FormField label="Select platform" htmlFor="miner-account-selector">
          <Select
            value={type || undefined}
            onValueChange={changeOutreachSourceType}
          >
            <SelectTrigger asChild>
              <FilterButton
                className="lg:w-48"
                isLoading={isLoadingIntegrationSettings}
              >
                <SelectValue placeholder="Select a platform" />
              </FilterButton>
            </SelectTrigger>

            <SelectContent>
              {typedObjectEntries(typeOptions).map(
                ([key, { label, icon: Icon }]) => {
                  const isDisabled = !integrationSettings?.[key];

                  return (
                    <SelectItem key={key} value={key} disabled={isDisabled}>
                      <Icon />
                      <SelectItemText>{label}</SelectItemText>
                    </SelectItem>
                  );
                },
              )}
            </SelectContent>
          </Select>
        </FormField>

        <FormField
          label="Select a source"
          htmlFor="miner-account-selector"
          hint="We will use this source to find targets for your campaign"
          className="w-full"
          error={
            crmSourcesError &&
            `Error loading sources${crmSourcesError instanceof HTTPError && `: ${crmSourcesError.serverMessage}`}`
          }
        >
          <DropdownMenu>
            <DropdownMenuTrigger disabled={!type} asChild>
              <SelectButton
                hasSelection={isEmailBison ? !!selectedSource : false}
                isLoading={iseLoadingCrmSources}
              >
                {isEmailBison && selectedSource
                  ? selectedSource.name
                  : "Select a targeting source"}
              </SelectButton>
            </DropdownMenuTrigger>

            <DropdownMenuContent className="w-[var(--radix-popper-anchor-width)]">
              {options.length > 2 && (
                <MenuSearchInput
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                />
              )}

              {dropdownContent}
            </DropdownMenuContent>
          </DropdownMenu>
        </FormField>

        <RenderIf condition={type === "emailbison"}>
          <FormField
            htmlFor="linkedin-url-variable-name"
            label="LinkedIn URL variable name"
            size="md"
            className="w-full"
          >
            <Input
              placeholder="e.g. linkedin_url"
              variant="md"
              id="linkedin-url-variable-name"
              value={linkedinUrlFieldName}
              onChange={(e) => setLinkedinUrlFieldName(e.target.value)}
            />
          </FormField>
        </RenderIf>
      </div>

      <RenderIf condition={type === "emailbison"}>
        <Button
          disabled={!canApply}
          variant="secondary-purple"
          size="sm"
          className="ml-auto mt-2"
          onClick={handleApply}
        >
          Apply
        </Button>
      </RenderIf>
    </section>
  );
}
